// Last-resort sentinel only: it is hit when neither the container's own `APP_VERSION`
// nor the build-time-inlined `NEXT_PUBLIC_APP_VERSION` is present (a misconfiguration).
// The real dev value is derived from `.appversion-prefix` by `versionEnv()` and arrives
// via `NEXT_PUBLIC_APP_VERSION`, so no app-version literal is hardcoded here.
const DEV_FALLBACK = '0.0.0-dev';

/**
 * Returns the app version.
 *
 * Client-side: the resolved global `appVersion`, provided by `AppVersionProvider` /
 * `window.__TN_APP_VERSION__` (injected by appshell during SSR).
 * When accessed outside the appshell tree (local dev / platypus direct MFE hits) it
 * falls back to this MFE's own build-time `NEXT_PUBLIC_APP_VERSION`, then to the sentinel.
 *
 * Server-side: the CONTAINER'S OWN build-time `APP_VERSION`. This is intentional for
 * OTEL `service.version` — startup traces/errors must correlate to the actual running
 * code, not the aggregated global version. When absent (local dev / feature instances,
 * which pass no version) it falls back to the build-time-inlined `NEXT_PUBLIC_APP_VERSION`
 * (derived from `.appversion-prefix` by `versionEnv()`), then to the sentinel — real builds
 * always set `APP_VERSION`, so this only affects dev. For appshell business logic that needs
 * the resolved global version, use `resolveGlobalVersion()`
 * (`apps/ptv-appshell/src/utils/resolveGlobalVersion.ts`).
 *
 * Empty-string env vars are treated as unset (`||`, not `??`) to match the repo
 * convention in `initObservability` — a blank `APP_VERSION` must fall through to the
 * next source rather than propagate an empty version into tracking/Datadog/JWT.
 */
export function getAppVersion(): string {
    if (typeof window !== 'undefined') {
        return window.__TN_APP_VERSION__ || process.env.NEXT_PUBLIC_APP_VERSION || DEV_FALLBACK;
    }
    return process.env.APP_VERSION || process.env.NEXT_PUBLIC_APP_VERSION || DEV_FALLBACK;
}

declare global {
    interface Window {
        __TN_APP_VERSION__?: string;
    }
}
